import React from "react";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  TextField,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from "@mui/material";
import { toggleCountrySelectDrawerOpen } from "../redux/uiSlice";
import CloseIcon from "@mui/icons-material/Close";
import { countriesActions } from "../redux/countriesSlice";

interface CountrySelectDrawerProps {}

const CountrySelectDrawer: React.FC<CountrySelectDrawerProps> = () => {
  const dispatch = useAppDispatch();
  const { countrySelectDrawerOpen } = useAppSelector((state) => state.ui);
  const { countries } = useAppSelector((state) => state.countries);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [filteredCountries, setFilteredCountries] =
    React.useState<Country[]>(countries);

  React.useEffect(() => {
    const filtered = countries.filter((country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCountries(filtered);
  }, [searchTerm, countries]);

  const handleClose = () => {
    dispatch(toggleCountrySelectDrawerOpen());
  };

  const handleCountrySelect = (country: Country) => {
    dispatch(countriesActions.setSelectedCountry(country));
    handleClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <Drawer
      anchor="bottom"
      open={countrySelectDrawerOpen}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 500 },
          margin: "0 auto",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          height: "auto",
          maxHeight: "60vh",
          boxSizing: "border-box",
          px: 2,
          pb: 2,
          pt: 1,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6">Select Country</Typography>
        <IconButton onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <TextField
        variant="outlined"
        size="small"
        placeholder="Search countries..."
        fullWidth
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
      />

      <List sx={{ width: "100%", overflow: "auto" }}>
        {filteredCountries.length > 0 ? (
          filteredCountries.map((country) => (
            <ListItem
              component="button"
              key={country.id}
              onClick={() => handleCountrySelect(country)}
              sx={{
                backgroundColor: "background.paper",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={country.flagUrl} // Assuming your country object has a flag URL
                  alt={`${country.name} flag`}
                  sx={{ width: 24, height: 24, mr: 2 }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={country.name}
                slotProps={{
                  primary: {
                    typography: {
                      variant: "body1",
                      color: "text.primary",
                    },
                  },
                }}
              />
            </ListItem>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No countries found
          </Typography>
        )}
      </List>
    </Drawer>
  );
};

export default CountrySelectDrawer;
